import { useCallback, useState } from 'react';
import { UpdatedFact } from '@/components/product-insights/fab-ui';
import { LayoutToolbar } from '@/components/layout';
import { LaeControlsBar } from '@/components/product-insights/lae-productivity/LaeControlsBar';
import { LAE_SCHOOLS_LAYOUT, SchoolsView } from '@/components/product-insights/lae-productivity/SchoolsView';
import { fmtRange } from '@/components/product-insights/lae-productivity/lae-format';
import { useLaeProductivityParams } from '@/components/product-insights/lae-productivity/lae-params';
import { useLaeProductivitySchools } from '@/components/product-insights/lae-productivity/use-lae-productivity-data';
import { PiDeniedPage, PiError, PiPageLayout } from '@/components/product-insights/pi-kit';
import { ApiError } from '@/lib/api';
import type { LaeProdCycleOption } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { usePageLayout } from '@/lib/use-page-layout';

/**
 * /product-insights/lae — LAE (Lead & Application Engagement). Since 2026-10-29 this is a port of
 * the LAE app's Productivity dashboard, Schools mode only (`features/productivity/ProductivityPage.jsx`):
 * controls bar, then key figures, trend and the Schools table (one surface, no tabs), plus the
 * school modal. Live read through EMERGE's `/product-insights/lae/productivity/schools` proxy;
 * nothing stored.
 *
 * URL state is owned by `useLaeProductivityParams` (`lae-params.ts`): `cycle`, `funnel`, `source`,
 * `p`, `d`, `from`, `to`, `school`, `schoolFunnel`. Every default is an absent key; nothing is
 * written on arrival. Stale `tab` / `q` keys from the old page are ignored.
 *
 * Deliberate differences from LAE:
 * - No ModeSwitch, Counsellors view, `useUrlPersistedFilters` or `isManager` redirect: the refusal
 *   is the `product-insights` read grant, checked before anything is fetched.
 * - `UpdatedFact` and `LayoutToolbar` sit in the page header actions (EMERGE convention), not in the
 *   controls bar; the controls bar is not sticky.
 * - The default cycle is the CURRENT one (owner 2026-10-29). The params hook needs the response's
 *   `cycles[]` to resolve it, and the request needs the params, so the page keeps the last list it
 *   received and hands it back on the next render. The first request carries the cycle guessed
 *   from India today; the previous-window read waits until the cycle is resolved, so a wrong guess
 *   never costs a second wasted request.
 * - The previous equal-length window only feeds the KPI deltas: a failed previous read is silent
 *   (no deltas), never an error on the page.
 * - A 403 from the proxy with nothing on screen is the permission refusal, not a retryable error.
 */

const PAGE_TITLE = 'Lead & Application Engagement';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message || fallback;
  return fallback;
}

function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

export function ProductInsightsLaePage() {
  const { ability } = useAuth();
  if (!ability.canRead('product-insights')) {
    return <PiDeniedPage product="lae" title="Lead & Application Engagement" />;
  }
  return <LaeContent />;
}

function LaeContent() {
  // The last cycles list received; read back into the params hook to resolve the current cycle.
  const [cycles, setCycles] = useState<readonly LaeProdCycleOption[] | null>(null);
  const params = useLaeProductivityParams({ cycles });
  const layout = usePageLayout('product-insights.lae', LAE_SCHOOLS_LAYOUT);

  const [reloadToken, setReloadToken] = useState(0);
  const schools = useLaeProductivitySchools(params.apiParams, reloadToken);
  const prev = useLaeProductivitySchools(params.cycleResolved ? params.prevParams : null, reloadToken);

  const receivedCycles = schools.data?.cycles ?? null;
  if (receivedCycles !== null && receivedCycles !== cycles) {
    setCycles(receivedCycles);
  }

  const refresh = useCallback(() => setReloadToken((n) => n + 1), []);

  const data = schools.data;
  const prevData = prev.error !== null ? null : prev.data;
  const hasError = schools.error !== null;
  const fetchedAt = schools.meta?.fetchedAt ?? null;

  if (data === null && isForbidden(schools.error)) {
    return <PiDeniedPage product="lae" title={PAGE_TITLE} />;
  }

  const facts = (
    <span className="tabular-nums">
      {fmtRange(params.range.from, params.range.to)} · India time
    </span>
  );

  return (
    <PiPageLayout
      product="lae"
      title={PAGE_TITLE}
      facts={facts}
      actions={
        <>
          <UpdatedFact fetchedAt={fetchedAt} refreshing={schools.refreshing} onRefresh={refresh} />
          <LayoutToolbar layout={layout} />
        </>
      }
    >
      <LaeControlsBar
        params={params}
        cycles={data?.cycles ?? null}
        funnels={data?.funnels ?? null}
        loading={data === null && !hasError}
      />

      {hasError && (
        <PiError
          message={errorMessage(schools.error, 'Lead & Application Engagement could not be reached.')}
          onRetry={refresh}
        />
      )}

      {(data !== null || !hasError) && (
        <SchoolsView
          data={data}
          prevData={prevData}
          refreshing={schools.refreshing}
          params={params}
          layout={layout}
        />
      )}
    </PiPageLayout>
  );
}

export default ProductInsightsLaePage;
